import React from 'react'
import PropTypes from 'prop-types'
import useMessageFormat from '@/hooks/intl/useMessageFormat'
import { Message } from '@paypalcorp/worldready-react'
import { BackButton, CloseButton } from '../buttons'
import { goBack, goToMyAccount } from '../../utils/navigate'
import { css } from '@emotion/core'
import { modalTitleCss, modalContentCss, modalContentHeaderCss } from './styles'
import PayPalLogo from '../common/PayPalLogo'
import { isGBDebitCardEnrollmentFlow } from 'utils/flow'
import { safeFormatMessage } from 'utils/safeFormatMessage'
import qs from 'query-string'
import { PRODUCT_NAMES } from 'universal/cfs-card'
import { FLOW_STEPS } from '@/constants/flow-steps'
const { CONSUMER_DEBIT_CARD } = PRODUCT_NAMES
const { FUNDING_INSTRUMENT_STEP } = FLOW_STEPS.ENROLL_CARD

function ModalNavBar({
  titleContent = {},
  stepper,
  hideCloseButton,
  showBackButton,
  onClose,
  onFirstStepBack,
  showTitle,
  closeDisabled = false,
}) {
  const { previousStep, isFirstStep, getCurrentStepName } = stepper as any
  const { formatMessage: formatMessageFn } = useMessageFormat(undefined)
  const formatMessage = formatMessageFn as any

  const headerRelativeCss = css`
    position: relative;
  `

  const logoOverlayCss = css`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
    pointer-events: none;
  `

  const isPPDCRequestCardFlow = () => {
    let intent = qs.parse(window.location.search).intent
    let productName = qs.parse(window.location.search).productName
    return (
      getCurrentStepName() === 'setPin' &&
      productName === CONSUMER_DEBIT_CARD &&
      intent === 'requestPhysicalCard'
    )
  }

  const handleCloseBtn = () => {
    if (onClose) {
      onClose()
      return
    }
    goToMyAccount()
  }

  const handleBackBtnClick = (e) => {
    e.preventDefault(0)

    if (isFirstStep()) {
      if (isPPDCRequestCardFlow()) {
        handleCloseBtn()
        return
      }

      if (onFirstStepBack) {
        onFirstStepBack()
        return
      }
      ;(goBack as any)()
      return
    }
    previousStep()
  }

  // Hide back button for specific funding instrument steps
  const currentStepName = getCurrentStepName()
  const shouldHideBackButtonForFundingSteps =
    isGBDebitCardEnrollmentFlow() && currentStepName === FUNDING_INSTRUMENT_STEP
  const finalShowBackButton = showBackButton && !shouldHideBackButtonForFundingSteps

  // Imperative formatMessage throws while WorldReady namespaces load (BDMC enroll ValueProp /
  // ConfirmCardAddress entry). Modal chrome mounts before those screens — an uncaught throw
  // blanks the whole flow via ErrorBoundary. Prefer declarative <Message> for the title and
  // safeFormatMessage for button labels.
  const backLabel = safeFormatMessage(formatMessage, 'app.button.back')
  const closeLabel = safeFormatMessage(formatMessage, 'funding.button.close')

  const BackButtonAny = BackButton as any
  const backButton = finalShowBackButton && (
    <BackButtonAny onClick={handleBackBtnClick} backtext={backLabel || undefined} />
  )

  const { id: titleContentId, values: titleContentValues = {} } = (titleContent || {}) as any

  return (
    <header className="ppvx_modal-header ppvx_modal-header--no-logo">
      <div css={css([modalContentCss, modalContentHeaderCss, headerRelativeCss])}>
        {/* Centered overlay logo above actions/title */}
        {/* eslint-disable-next-line i18n-scan/no-country-dependency -- GB-specific PayPal logo overlay for compliance */}
        {isGBDebitCardEnrollmentFlow() && (
          <div css={logoOverlayCss}>
            <PayPalLogo height="30px" style={{ padding: 0 }} />
          </div>
        )}
        <div className="ppvx_modal-header__action">{finalShowBackButton && backButton}</div>
        {showTitle && titleContentId && (
          <div
            className="ppvx_modal-header__title"
            data-testid="modal-title"
            id="modal-header"
            css={modalTitleCss}
          >
            <Message id={titleContentId} {...titleContentValues} />
          </div>
        )}
        {hideCloseButton ? (
          <div className="ppvx_modal-header__action" />
        ) : (
          <CloseButton
            onClick={handleCloseBtn}
            disabled={closeDisabled}
            ariaLabel={closeLabel || undefined}
          />
        )}
      </div>
    </header>
  )
}

ModalNavBar.propTypes = {
  titleContent: PropTypes.object,
  stepper: PropTypes.object.isRequired,
  onClose: PropTypes.func,
  onFirstStepBack: PropTypes.func,
  hideCloseButton: PropTypes.bool,
  showBackButton: PropTypes.bool,
  showTitle: PropTypes.bool,
  closeDisabled: PropTypes.bool,
}

export default ModalNavBar
